import type { InvoiceExtractionIn } from '@/api/invoices';
import type { FindingOut } from '@/api/types';

/** The subset of invoice-editor fields we can pre-fill from the GL
 *  transaction (the finding's evidence). Invoice # is deliberately
 *  absent — the vendor invoice number lives on the paper, not in the
 *  ledger, so there's nothing to seed it from. */
export interface InvoiceFactsPrefill {
  supplier_name_on_invoice: string | null;
  supplier_bn: string | null;
  invoice_date: string | null;
  invoice_total: string | null;
}

function evidenceString(evidence: Record<string, unknown>, key: string): string | null {
  const v = evidence[key];
  if (typeof v === 'string') {
    const trimmed = v.trim();
    return trimmed === '' ? null : trimmed;
  }
  if (typeof v === 'number') return String(v);
  return null;
}

/** Prefill values from a finding's evidence (its GL transaction).
 *  These are the ledger-side facts every rule snapshots:
 *  `supplier.name`, `bill_date`, `bill_amount`, and — when the supplier
 *  has a captured federal BN — `supplier.bn`. */
export function invoiceFactsPrefill(evidence: Record<string, unknown>): InvoiceFactsPrefill {
  return {
    supplier_name_on_invoice: evidenceString(evidence, 'supplier.name'),
    supplier_bn: evidenceString(evidence, 'supplier.bn'),
    invoice_date: evidenceString(evidence, 'bill_date'),
    invoice_total: evidenceString(evidence, 'bill_amount'),
  };
}

function isBlank(v: unknown): boolean {
  return v === null || v === undefined || (typeof v === 'string' && v.trim() === '');
}

/** Merge prefill into the current editor draft as a fill-blanks-only
 *  fallback: an OCR-extracted value or an already-saved link fact always
 *  wins over the GL prefill. Only ever touches the four prefillable
 *  fields — every other field passes through untouched. */
export function seedDraftWithPrefill(
  current: InvoiceExtractionIn,
  prefill: InvoiceFactsPrefill,
): InvoiceExtractionIn {
  return {
    ...current,
    supplier_name_on_invoice: isBlank(current.supplier_name_on_invoice)
      ? prefill.supplier_name_on_invoice
      : current.supplier_name_on_invoice,
    supplier_bn: isBlank(current.supplier_bn) ? prefill.supplier_bn : current.supplier_bn,
    invoice_date: isBlank(current.invoice_date) ? prefill.invoice_date : current.invoice_date,
    invoice_total: isBlank(current.invoice_total)
      ? prefill.invoice_total
      : current.invoice_total,
  };
}

/** Distinct, alphabetically-sorted supplier names across a set of
 *  findings — the searchable options for the supplier field. */
export function distinctSupplierNames(findings: FindingOut[]): string[] {
  const names = new Set<string>();
  for (const f of findings) {
    const name = evidenceString(f.evidence, 'supplier.name');
    if (name) names.add(name);
  }
  return [...names].sort((a, b) => a.localeCompare(b));
}
